import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../api/axiosInstance";
import BlogSeoSchema, {
  mergeSeo,
  mergeSchema,
  checkSocialImage,
} from "../blog/BlogSeoSchema";

import {
  TEXT_RULES,
  REPEATER_RULES,
  validateCaseStudyForm,
  getTabForError,
  validateImageBeforeUpload,
} from "./components/CaseStudyValidation"

const SITE_URL = ("https://procman.ai").replace(/\/$/, "");
const API = "/api/superadmin/case-studies-detail/";
const INDUSTRY_API = "/api/superadmin/case-study-industries/";

const EMPTY_DETAIL = {
  results_achieved: {
    enabled: true,
    title: "Results Achieved",
    subtitle: "",
    items: [{ value: "", label: "" }],
  },
  company_overview: {
    enabled: true,
    title: "Company Overview",
    description: "",
    industry_label: "Industry",
    industry_value: "",
    employees: "",
    annual_spend: "",
  },
  challenge: {
    enabled: true,
    title: "The Challenge",
    items: [{ title: "", description: "" }],
  },
  solution: {
    enabled: true,
    title: "The Solution",
    items: [{ title: "", description: "" }],
  },
  implementation_journey: {
    enabled: true,
    title: "Implementation Journey",
    subtitle: "",
    items: [{ phase: "Phase 1", title: "", description: "" }],
  },
  before_after: {
    enabled: true,
    title: "Before Procman AI vs After Procman AI",
    before: [{ title: "", description: "" }],
    after: [{ title: "", description: "" }],
  },
  customer_review: {
    enabled: true,
    rating: 5,
    testimonial: "",
    reviewer_name: "",
    designation: "",
    company: "",
  },
  faqs: {
    enabled: true,
    title: "Frequently Asked Questions",
    items: [{ question: "", answer: "" }],
  },
};

const EMPTY_FORM = {
  title: "",
  slug: "",
  case_study_type: "default",
  industry: "",
  company: "",
  description: "",
  featured: false,
  is_published: false,
  other_data: {
    author_name: "",
    published_date: "",
  },
  detail_page: EMPTY_DETAIL,
};

// og_image / twitter_image are handled by the SEO & Schema tab
const IMAGE_FIELDS = [
  ["banner_image", "Hero banner", "Recommended 1600×600"],
  ["featured_image", "Featured / listing image", "Shown on the case studies list"],
  ["company_logo", "Company logo", "Square or transparent PNG works best"],
  ["case_study_pdf", "Case study PDF", "Optional downloadable PDF"],
];

const TABS = [
  ["basic", "Basic Information"],
  ["hero", "Images"],
  ["results", "Results Achieved"],
  ["company", "Company Overview"],
  ["challenge", "Challenge & Solution"],
  ["journey", "Implementation Journey"],
  ["before_after", "Before & After"],
  ["review", "Customer Review"],
  ["faqs", "FAQs"],
  ["seo", "SEO & Schema"],
];

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function formatApiError(data) {
  if (!data) return "Unable to save case study.";
  if (typeof data === "string") return data;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  if (data.errors) {
    return Object.entries(data.errors)
      .map(([k, v]) => `${k}: ${v}`)
      .join("\n");
  }
  return JSON.stringify(data);
}

/* ---------- small UI pieces ---------- */

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-purple-600 focus:ring-2 focus:ring-purple-100";

const inputErrorClass = inputClass
  .replace("border-slate-300", "border-red-500")
  .replace("focus:border-purple-600 focus:ring-2 focus:ring-purple-100", "focus:border-red-600 focus:ring-2 focus:ring-red-100");

function Field({ label, hint, children, rules, error, length }) {
  return (
    <label className="block space-y-1.5">
      <span className="flex items-baseline justify-between gap-2 text-sm font-medium text-slate-700">
        <span>{label}</span>
        {rules?.max && (
          <span className={`text-xs ${error ? "text-red-600" : "text-slate-500"}`}>
            {length}/{rules.max}
          </span>
        )}
      </span>
      {children}
      {error ? (
        <span className="block text-xs font-medium text-red-600">{error}</span>
      ) : (
        (hint || rules?.help) && <span className="block text-xs text-slate-500">{hint || rules.help}</span>
      )}
    </label>
  );
}

function TextInput({ label, value, onChange, hint, rules, error, ...props }) {
  return (
    <Field label={label} hint={hint} rules={rules} error={error} length={(value ?? "").toString().trim().length}>
      <input
        className={error ? inputErrorClass : inputClass}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        {...props}
      />
    </Field>
  );
}

function TextArea({ label, value, onChange, rows = 3, hint, rules, error }) {
  return (
    <Field label={label} hint={hint} rules={rules} error={error} length={(value ?? "").toString().trim().length}>
      <textarea
        className={error ? inputErrorClass : inputClass}
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 rounded-lg bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-700">
      <input
        type="checkbox"
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-purple-600"
      />
      {label}
    </label>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="border-b border-slate-100 pb-4">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
  );
}

function getCleanS3Url(fileUrl) {
  if (!fileUrl) return "";

  try {
    const url = new URL(fileUrl);

    url.searchParams.delete("response-content-disposition");
    url.searchParams.delete("response-content-type");

    return url.toString();
  } catch {
    return fileUrl;
  }
}
function FileUpload({ label, hint, accept, file, existingUrl, onChange, isPdf, error }) {
  const previewUrl = useMemo(
    () => (file && !isPdf ? URL.createObjectURL(file) : ""),
    [file, isPdf]
  );

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const shownImage = previewUrl || (!isPdf ? existingUrl : "");

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-start gap-4">
        <div className="flex h-20 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-400">
          {shownImage ? (
            <img src={shownImage} alt={label} className="h-full w-full object-cover" />
          ) : isPdf ? (
            "PDF"
          ) : (
            "No image"
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <p className="text-sm font-medium text-slate-800">{label}</p>
            {hint && <p className="text-xs text-slate-500">{hint}</p>}
          </div>

          <input
            type="file"
            accept={accept}
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              e.target.value = "";
              onChange(f);
            }}
            className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-purple-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-purple-700 hover:file:bg-purple-100"
          />

          {error && <p className="text-xs font-medium text-red-600">{error}</p>}

          {file && (
            <p className="truncate text-xs text-green-700">Selected: {file.name}</p>
          )}

          {!file && existingUrl && (
            <a
              href={
                isPdf
                  ? getCleanS3Url(existingUrl)
                  : existingUrl
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-purple-700 underline"
            >
              View current {isPdf ? "PDF" : "image"}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function RepeatableFields({ title, items, onChange, createItem, fields, path, ruleKey, errors = {} }) {
  const repeaterRules = REPEATER_RULES[path];
  const countError = errors[path];
  const noun = repeaterRules?.noun || "item";
  const atMax = Boolean(repeaterRules?.max && items.length >= repeaterRules.max);
  const atMin = Boolean(repeaterRules?.min && items.length <= repeaterRules.min);

  function updateItem(index, key, value) {
    onChange(items.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  }
  function removeItem(index) {
    if (atMin) return;
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">{title}</h3>
        <div className="flex items-center gap-3">
          {repeaterRules && (
            <span className={`text-xs font-semibold ${countError ? "text-red-600" : "text-slate-500"}`}>
              {items.length}/{repeaterRules.max} · min {repeaterRules.min}
            </span>
          )}
          <button
            type="button"
            disabled={atMax}
            onClick={() => !atMax && onChange([...items, createItem()])}
            className="rounded-lg bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            + Add {noun}
          </button>
        </div>
      </div>

      {countError && <p className="text-xs font-medium text-red-600">{countError}</p>}

      {items.map((item, index) => (
        <div key={index} className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/50 p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Item {index + 1}
            </span>
            <button
              type="button"
              disabled={atMin}
              onClick={() => removeItem(index)}
              className="text-xs font-medium text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Remove
            </button>
          </div>

          {fields.map((field) => {
            const rules = TEXT_RULES[`${ruleKey}.${field.key}`];
            const err = errors[`${path}[${index}].${field.key}`];
            const val = item[field.key] ?? "";
            const cls = err ? inputErrorClass : inputClass;
            return (
              <Field key={field.key} label={field.label} rules={rules} error={err} length={String(val).trim().length}>
                {field.multiline ? (
                  <textarea className={cls} rows={3} value={val} onChange={(e) => updateItem(index, field.key, e.target.value)} />
                ) : (
                  <input className={cls} value={val} onChange={(e) => updateItem(index, field.key, e.target.value)} />
                )}
              </Field>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ---------- main component ---------- */

export default function CaseStudyForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState({});
  const [existingImages, setExistingImages] = useState({});
  const [slugEdited, setSlugEdited] = useState(Boolean(id));
  const [industries, setIndustries] = useState([]);

  const [submitted, setSubmitted] = useState(false);
  const [fileErrors, setFileErrors] = useState({});

  const errors = useMemo(
    () => validateCaseStudyForm(form, { strict: submitted }),
    [form, submitted]
  );
  const tabsWithErrors = useMemo(
    () => new Set(Object.keys(errors).map(getTabForError)),
    [errors]
  );
  // shortcut: <TextInput {...fp("title")} />
  const fp = (key) => ({ rules: TEXT_RULES[key], error: errors[key] });

  // SEO & Schema (same as blog/case-study editor)
  const [seo, setSeo] = useState(mergeSeo({}));
  const [schema, setSchema] = useState(mergeSchema({}));
  const [ogPreview, setOgPreview] = useState("");
  const [twitterPreview, setTwitterPreview] = useState("");
  const [seoErrors, setSeoErrors] = useState({});
  const [canonicalEdited, setCanonicalEdited] = useState(false);

  // Industries dropdown
  useEffect(() => {
    let cancelled = false;

    axiosInstance
      .get(INDUSTRY_API)
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.results ?? data?.data ?? [];
        setIndustries(list.filter((item) => item.is_active !== false));
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load industries.");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (canonicalEdited || !form.slug) return;
    setSeo((prev) => ({
      ...prev,
      canonical_url: `${SITE_URL}/case-studies/${form.slug}`,
    }));
  }, [form.slug, canonicalEdited]);

  // Load existing case study
  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    async function loadCaseStudy() {
      try {
        const response = await axiosInstance.get(`${API}${id}/`);
        if (cancelled) return;

        const data = response.data?.data ?? response.data;
        const detail = data.detail_page || {};

        setForm({
          ...EMPTY_FORM,
          ...data,
          industry: data.industry != null ? String(data.industry) : "",
          // company: data.company != null ? String(data.company) : "",
          company:
            data.other_data?.company_name ??
            data.company_name ??
            (typeof data.company === "string" ? data.company : ""),
          other_data: {
            ...EMPTY_FORM.other_data,
            ...(data.other_data || {}),
          },
          detail_page: {
            ...EMPTY_DETAIL,
            ...detail,
          },
        });

        setSeo(mergeSeo(detail.seo));
        setCanonicalEdited(Boolean(detail.seo?.canonical_url));
        setSchema(mergeSchema(detail.schema));
        setOgPreview(data.og_image || "");
        setTwitterPreview(data.twitter_image || "");

        setExistingImages({
          banner_image: data.banner_image,
          featured_image: data.featured_image,
          company_logo: data.company_logo,
          og_image: data.og_image,
          twitter_image: data.twitter_image,
          case_study_pdf: data.case_study_pdf,
        });
      } catch (err) {
        if (!cancelled) {
          setError(formatApiError(err.response?.data) || "Unable to load the case study.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadCaseStudy();

    return () => {
      cancelled = true;
    };
  }, [id]);


  function handleSeoChange(next) {
    if (next?.canonical_url !== seo.canonical_url) setCanonicalEdited(true);
    setSeo(next);
  }
  function updateField(key, value) {
    setForm((previous) => ({ ...previous, [key]: value }));
  }

  function updateOtherData(key, value) {
    setForm((previous) => ({
      ...previous,
      other_data: { ...previous.other_data, [key]: value },
    }));
  }

  function updateSection(section, key, value) {
    setForm((previous) => ({
      ...previous,
      detail_page: {
        ...previous.detail_page,
        [section]: {
          ...previous.detail_page[section],
          [key]: value,
        },
      },
    }));
  }

  function handleTitleChange(value) {
    setForm((previous) => ({
      ...previous,
      title: value,
      slug: slugEdited ? previous.slug : slugify(value),
    }));
  }

  async function handleFileChange(field, file) {
    if (file) {
      const problems = await validateImageBeforeUpload(file, field);
      if (problems.length) {
        setFileErrors((p) => ({ ...p, [field]: problems.join(" ") }));
        return;
      }
    }
    setFileErrors((p) => ({ ...p, [field]: null }));
    setSelectedFiles((previous) => ({ ...previous, [field]: file || null }));
  }

  const pickSocialImage = (key, setPreview) => async (file) => {
    const err = await checkSocialImage(file);
    setSeoErrors((s) => ({ ...s, [key]: err }));
    if (err) return;
    setSelectedFiles((s) => ({ ...s, [key]: file }));
    setPreview(URL.createObjectURL(file));
  };

  function validateForm() {
    if (!form.title.trim()) return "Please enter a case study title.";
    if (!form.slug.trim()) return "Please enter a URL slug.";
    if (!String(form.industry).trim()) return "Please select an industry.";
    if (!form.description.trim()) return "Please enter a description.";

    if (schema.custom_json?.trim()) {
      try {
        JSON.parse(schema.custom_json);
        setSeoErrors((s) => ({ ...s, schema_custom_json: null }));
      } catch {
        setSeoErrors((s) => ({ ...s, schema_custom_json: "Invalid JSON" }));
        setActiveTab("seo");
        return "Schema custom JSON is invalid.";
      }
    }

    return "";
  }

  async function handleSave(publish = false) {
    setError("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      if (activeTab !== "seo") setActiveTab("basic");
      return;
    }

    const fieldErrors = validateCaseStudyForm(form, { strict: publish });
if (publish) setSubmitted(true);
const errorKeys = Object.keys(fieldErrors);
if (errorKeys.length) {
  setError(`Please fix ${errorKeys.length} field(s) before ${publish ? "publishing" : "saving"}.`);
  setActiveTab(getTabForError(errorKeys[0]));
  return;
}

    setSaving(true);

    try {
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim(),
        case_study_type: form.case_study_type,
        industry: Number(form.industry),
        // company: form.company ? Number(form.company) : "",
        description: form.description,
        featured: form.featured,
        is_published: publish,
        detail_page: { ...form.detail_page, seo, schema },
        other_data: form.other_data,
      };

      const body = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (key === "detail_page" || key === "other_data") {
          body.append(key, JSON.stringify(value));
        } else {
          body.append(key, String(value));
        }
      });

      Object.entries(selectedFiles).forEach(([key, file]) => {
        if (file) body.append(key, file);
      });

      // IMPORTANT: without this, files are not sent correctly
      const cfg = { headers: { "Content-Type": "multipart/form-data" } };

      const response = id
        ? await axiosInstance.patch(`${API}${id}/`, body, cfg)
        : await axiosInstance.post(API, body, cfg);

      const saved = response.data?.data ?? response.data;

      if (publish && !saved.is_published) {
        await axiosInstance.post(`${API}${saved.id}/publish/`);
      }

      navigate("/cms/case-studies");
    } catch (err) {
      setError(formatApiError(err.response?.data));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="p-8 text-slate-600">Loading case study...</div>;
  }

  const detail = form.detail_page;
  const tabIndex = TABS.findIndex(([key]) => key === activeTab);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-8">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => navigate("/cms/case-studies")}
              className="text-xs font-medium text-slate-500 hover:text-slate-800"
            >
              ← Back to Case Studies
            </button>
            <h1 className="truncate text-xl font-bold text-slate-900">
              {id ? "Edit Case Study" : "Create Case Study"}
            </h1>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSave(false)}
              className="rounded-lg border border-purple-300 bg-white px-4 py-2 text-sm font-medium text-purple-800 hover:bg-purple-50 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Draft"}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSave(true)}
              className="rounded-lg bg-purple-700 px-4 py-2 text-sm font-medium text-white hover:bg-purple-800 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Publish"}
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl space-y-5 p-4 md:p-8">
        {error && (
          <div className="whitespace-pre-wrap rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
          {/* Sidebar tabs */}
          <aside className="h-fit rounded-xl border border-slate-200 bg-white p-2 lg:sticky lg:top-24">
            {TABS.map(([key, label], i) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`mb-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition ${activeTab === key
                  ? "bg-purple-50 font-semibold text-purple-800"
                  : "text-slate-600 hover:bg-slate-50"
                  }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${activeTab === key
                    ? "bg-purple-700 text-white"
                    : "bg-slate-200 text-slate-600"
                    }`}
                >
                  {i + 1}
                </span>
                {label}
                {tabsWithErrors.has(key) && <span className="ml-auto h-2 w-2 rounded-full bg-red-500" />}
              </button>
            ))}
          </aside>

          <main className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:p-7">
            {activeTab === "basic" && (
              <div className="space-y-5">
                <SectionHeader
                  title="Basic Information"
                  subtitle="Core details shown on the case study listing and detail page."
                />

                <TextInput
                  label="Case study title *"
                  value={form.title}
                  onChange={handleTitleChange}
                  {...fp("title")}
                />

                <TextInput
                  label="URL slug *"
                  value={form.slug}
                  onChange={(value) => {
                    setSlugEdited(true);
                    updateField("slug", slugify(value));
                  }}
                  {...fp("slug")}
                  hint="Auto-generated from the title. Edit to customise."
                />

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <Field label="Industry *">
                    <select
                      className={inputClass}
                      value={form.industry}
                      onChange={(e) => updateField("industry", e.target.value)}
                    >
                      <option value="">Select industry</option>
                      {industries.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Case study type">
                    <select
                      className={inputClass}
                      value={form.case_study_type}
                      onChange={(e) =>
                        updateField("case_study_type", e.target.value)
                      }
                    >
                      <option value="default">Default</option>
                      <option value="buyer">Buyer</option>
                      <option value="supplier">Supplier</option>
                    </select>
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <TextInput
                    label="Author name"
                    value={form.other_data?.author_name}
                    onChange={(value) => updateOtherData("author_name", value)}
                    {...fp("author_name")}
                    placeholder="Example: Rahul Sharma"
                  />

                  <Field label="Published date">
                    <input
                      type="date"
                      className={inputClass}
                      value={form.other_data?.published_date || ""}
                      onChange={(e) =>
                        updateOtherData("published_date", e.target.value)
                      }
                    />
                  </Field>
                </div>

                {/* <TextInput
                  label="Company / VendorProfile ID (optional)"
                  value={form.company}
                  onChange={(value) => updateField("company", value)}
                  inputMode="numeric"
                  placeholder="Example: 10"
                /> */}
                <TextInput
                  label="Company Name"
                  value={form.other_data?.company_name || ""}
                  onChange={(value) => updateOtherData("company_name", value)}
                  {...fp("company_name")}
                  placeholder="Example: AVI"
                />

                <TextArea
                  label="Short description *"
                  value={form.description}
                  onChange={(value) => updateField("description", value)}
                  {...fp("description")}
                  rows={4}
                />

                <Toggle
                  label="Featured case study"
                  checked={form.featured}
                  onChange={(value) => updateField("featured", value)}
                />

                <p className="text-xs text-slate-500">
                  Save as a draft first. Publish after all required sections
                  are completed.
                </p>
              </div>
            )}

            {activeTab === "hero" && (
              <div className="space-y-5">
                <SectionHeader
                  title="Images"
                  subtitle="Upload the images and PDF used on the case study pages. Social (OG / Twitter) images are in the SEO & Schema tab."
                />

                {IMAGE_FIELDS.map(([key, label, hint]) => (
                  <FileUpload
                    key={key}
                    label={label}
                    hint={hint}
                    isPdf={key === "case_study_pdf"}
                    accept={key === "case_study_pdf" ? ".pdf,application/pdf" : "image/*"}
                    file={selectedFiles[key]}
                    existingUrl={existingImages[key]}
                    onChange={(file) => handleFileChange(key, file)}
                    error={fileErrors[key]} 
                  />
                ))}
              </div>
            )}

            {activeTab === "results" && (
              <div className="space-y-5">
                <SectionHeader title="Results Achieved" />

                <Toggle
                  label="Show Results Achieved section"
                  checked={detail.results_achieved.enabled}
                  onChange={(value) =>
                    updateSection("results_achieved", "enabled", value)
                  }
                />

                <TextInput
                  label="Section title"
                  value={detail.results_achieved.title}
                  onChange={(value) =>
                    updateSection("results_achieved", "title", value)
                  }
                  {...fp("results_achieved.title")}
                />

                <TextInput
                  label="Subtitle"
                  value={detail.results_achieved.subtitle}
                  onChange={(value) =>
                    updateSection("results_achieved", "subtitle", value)
                  }
                  {...fp("results_achieved.subtitle")}
                />

                <RepeatableFields
                  title="Metrics"
                  items={detail.results_achieved.items}
                  onChange={(value) =>
                    updateSection("results_achieved", "items", value)
                  }

                  createItem={() => ({ value: "", label: "" })}
                  fields={[
                    { key: "value", label: "Metric value" },
                    { key: "label", label: "Metric label" },
                  ]}
                  path="results_achieved.items" ruleKey="results_achieved.item" errors={errors}
                />
              </div>
            )}

            {activeTab === "company" && (
              <div className="space-y-5">
                <SectionHeader title="Company Overview" />

                <Toggle
                  label="Show Company Overview section"
                  checked={detail.company_overview.enabled}
                  onChange={(value) =>
                    updateSection("company_overview", "enabled", value)
                  }
                />

                <TextInput
                  label="Section title"
                  value={detail.company_overview.title}
                  onChange={(value) =>
                    updateSection("company_overview", "title", value)
                  }
                />

                <TextArea
                  label="Company overview description"
                  value={detail.company_overview.description}
                  onChange={(value) =>
                    updateSection("company_overview", "description", value)
                  }
                  {...fp("company_overview.description")}
                />

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <TextInput
                    label="Industry label"
                    value={detail.company_overview.industry_label}
                    onChange={(value) =>
                      updateSection("company_overview", "industry_label", value)
                    }
                  />

                  <TextInput
                    label="Industry value"
                    value={detail.company_overview.industry_value}
                    onChange={(value) =>
                      updateSection("company_overview", "industry_value", value)
                    }
                  />

                  <TextInput
                    label="Number of employees"
                    value={detail.company_overview.employees}
                    onChange={(value) =>
                      updateSection("company_overview", "employees", value)
                    }
                  />

                  <TextInput
                    label="Annual spend"
                    value={detail.company_overview.annual_spend}
                    onChange={(value) =>
                      updateSection("company_overview", "annual_spend", value)
                    }
                  />
                </div>
              </div>
            )}

            {activeTab === "challenge" && (
              <div className="space-y-8">
                <section className="space-y-4">
                  <SectionHeader title="The Challenge" />

                  <Toggle
                    label="Show Challenge section"
                    checked={detail.challenge.enabled}
                    onChange={(value) =>
                      updateSection("challenge", "enabled", value)
                    }
                  />

                  <TextInput
                    label="Challenge section title"
                    value={detail.challenge.title}
                    onChange={(value) =>
                      updateSection("challenge", "title", value)
                    }
                  />

                  <RepeatableFields
                    title="Challenges"
                    items={detail.challenge.items}
                    onChange={(value) =>
                      updateSection("challenge", "items", value)
                    }
                    createItem={() => ({ title: "", description: "" })}
                    fields={[
                      { key: "title", label: "Challenge title" },
                      // { key: "description", label: "Description", multiline: true },
                    ]}
                    path="challenge.items" ruleKey="challenge.item" errors={errors}
                  />
                </section>

                <section className="space-y-4">
                  <SectionHeader title="The Solution" />

                  <Toggle
                    label="Show Solution section"
                    checked={detail.solution.enabled}
                    onChange={(value) =>
                      updateSection("solution", "enabled", value)
                    }
                  />

                  <TextInput
                    label="Solution section title"
                    value={detail.solution.title}
                    onChange={(value) =>
                      updateSection("solution", "title", value)
                    }
                  />

                  <RepeatableFields
                    title="Solutions"
                    items={detail.solution.items}
                    onChange={(value) =>
                      updateSection("solution", "items", value)
                    }
                    createItem={() => ({ title: "", description: "" })}
                    fields={[
                      { key: "title", label: "Solution title" },
                      // { key: "description", label: "Description", multiline: true },
                    ]}
                    path="solution.items" ruleKey="solution.item" errors={errors}
                  />
                </section>
              </div>
            )}

            {activeTab === "journey" && (
              <div className="space-y-5">
                <SectionHeader title="Implementation Journey" />

                <Toggle
                  label="Show Implementation Journey"
                  checked={detail.implementation_journey.enabled}
                  onChange={(value) =>
                    updateSection("implementation_journey", "enabled", value)
                  }
                />

                <TextInput
                  label="Section title"
                  value={detail.implementation_journey.title}
                  onChange={(value) =>
                    updateSection("implementation_journey", "title", value)
                  }
                />

                <TextInput
                  label="Subtitle"
                  value={detail.implementation_journey.subtitle}
                  onChange={(value) =>
                    updateSection("implementation_journey", "subtitle", value)
                  }
                />

                <RepeatableFields
                  title="Implementation phases"
                  items={detail.implementation_journey.items}
                  onChange={(value) =>
                    updateSection("implementation_journey", "items", value)
                  }
                  createItem={() => ({
                    phase: `Phase ${detail.implementation_journey.items.length + 1}`,
                    title: "",
                    description: "",
                  })}
                  fields={[
                    { key: "phase", label: "Phase label" },
                    { key: "title", label: "Phase title" },
                    { key: "description", label: "Description", multiline: true },
                  ]}
                  path="implementation_journey.items" ruleKey="implementation_journey.item" errors={errors}
                />
              </div>
            )}

            {activeTab === "before_after" && (
              <div className="space-y-8">
                <SectionHeader title="Before & After" />

                <Toggle
                  label="Show Before & After section"
                  checked={detail.before_after.enabled}
                  onChange={(value) =>
                    updateSection("before_after", "enabled", value)
                  }
                />

                <TextInput
                  label="Section title"
                  value={detail.before_after.title}
                  onChange={(value) =>
                    updateSection("before_after", "title", value)
                  }
                />

                <RepeatableFields
                  title="Before Procman AI"
                  items={detail.before_after.before}
                  onChange={(value) =>
                    updateSection("before_after", "before", value)
                  }
                  createItem={() => ({ title: "", description: "" })}
                  fields={[
                    { key: "title", label: "Item title" },
                    // { key: "description", label: "Description", multiline: true },
                  ]}

                  path="before_after.before" ruleKey="before_after.item" errors={errors}
                />

                <RepeatableFields
                  title="After Procman AI"
                  items={detail.before_after.after}
                  onChange={(value) =>
                    updateSection("before_after", "after", value)
                  }
                  createItem={() => ({ title: "", description: "" })}
                  fields={[
                    { key: "title", label: "Item title" },
                    // { key: "description", label: "Description", multiline: true },
                  ]}
                  path="before_after.after" ruleKey="before_after.item" errors={errors}
                />
              </div>
            )}

            {activeTab === "review" && (
              <div className="space-y-5">
                <SectionHeader title="Customer Review" />

                <Toggle
                  label="Show Customer Review"
                  checked={detail.customer_review.enabled}
                  onChange={(value) =>
                    updateSection("customer_review", "enabled", value)
                  }
                />

                <Field label="Rating (1–5)">
                  <select
                    className={inputClass}
                    value={detail.customer_review.rating}
                    onChange={(e) =>
                      updateSection(
                        "customer_review",
                        "rating",
                        Number(e.target.value)
                      )
                    }
                  >
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <option key={rating} value={rating}>
                        {rating} / 5
                      </option>
                    ))}
                  </select>
                </Field>

                <TextArea
                  label="Customer testimonial"
                  value={detail.customer_review.testimonial}
                  onChange={(value) =>
                    updateSection("customer_review", "testimonial", value)
                  }
                />

                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                  <TextInput
                    label="Reviewer name"
                    value={detail.customer_review.reviewer_name}
                    onChange={(value) =>
                      updateSection("customer_review", "reviewer_name", value)
                    }
                  />

                  <TextInput
                    label="Designation"
                    value={detail.customer_review.designation}
                    onChange={(value) =>
                      updateSection("customer_review", "designation", value)
                    }
                  />

                  <TextInput
                    label="Company"
                    value={detail.customer_review.company}
                    onChange={(value) =>
                      updateSection("customer_review", "company", value)
                    }
                  />
                </div>
              </div>
            )}

            {activeTab === "faqs" && (
              <div className="space-y-5">
                <SectionHeader title="FAQs" />

                <Toggle
                  label="Show FAQs section"
                  checked={detail.faqs.enabled}
                  onChange={(value) => updateSection("faqs", "enabled", value)}
                />

                <TextInput
                  label="Section title"
                  value={detail.faqs.title}
                  onChange={(value) => updateSection("faqs", "title", value)}
                />

                <RepeatableFields
                  title="Frequently Asked Questions"
                  items={detail.faqs.items}
                  onChange={(value) => updateSection("faqs", "items", value)}
                  createItem={() => ({ question: "", answer: "" })}
                  fields={[
                    { key: "question", label: "Question" },
                    { key: "answer", label: "Answer", multiline: true },
                  ]}
                  path="faqs.items" ruleKey="faqs.item" errors={errors}
                />
              </div>
            )}

            {activeTab === "seo" && (
              <BlogSeoSchema
                seo={seo}
                schema={schema}
                // onSeoChange={setSeo}
                onSeoChange={handleSeoChange}
                onSchemaChange={setSchema}
                errors={seoErrors}
                ogPreview={ogPreview}
                twitterPreview={twitterPreview}
                featuredPreview={
                  selectedFiles.featured_image
                    ? URL.createObjectURL(selectedFiles.featured_image)
                    : existingImages.featured_image
                }
                onOgImage={pickSocialImage("og_image", setOgPreview)}
                onTwitterImage={pickSocialImage("twitter_image", setTwitterPreview)}
              />
            )}

            <div className="mt-8 flex flex-wrap justify-between gap-3 border-t border-slate-100 pt-5">
              <button
                type="button"
                disabled={tabIndex === 0}
                onClick={() => setActiveTab(TABS[tabIndex - 1][0])}
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                ← Previous
              </button>

              <button
                type="button"
                disabled={tabIndex === TABS.length - 1}
                onClick={() => setActiveTab(TABS[tabIndex + 1][0])}
                className="rounded-lg bg-slate-900 px-4 py-2.5 text-sm text-white hover:bg-slate-800 disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}